import { ScanCriteriaLevel, ScanCriteriaType } from ".";
import { EtySubscribedModuleFeature } from "./entities";
import { EtyModuleFeature, IModuleWithFeatures } from "./outside";

export interface ISignUpCreds {
    name: string;
    email: string;
    password: string;
    agent_name: string;
};

export interface IEmailPassword {
    email: string;
    password: string;
};

export interface IPassword {
    password: string;
};

export enum MediaSourceType {
    UPLOADED = "uploaded",
    CREATED = "created",
};

export enum MediaSource {
    RESUME = "resume",
    INTERVIEW_QUESTION_RESPONSE = "interview-question-response",
    COVER_LETTER = "cover-letter",
};

export enum MediaType {
    PDF = "pdf",
    DOCX = "docx",
    AUDIO = "audio",
    VIDEO = "video",
};

export interface IMedia {
    id: number;
    name: string;
    source_id: number;
    source: MediaSource;
    source_type: MediaSourceType;
    type: MediaType;
    url: string;
}

interface ICoverLetterContent {
    id: number;
    cover_letter_id: number;
    original: string;
    ai: string;
    selection: string;
    type: string;
    hidden: boolean;
}

interface ICompanyDetail {
    id: number;
    cover_letter_id: number;
    contact_name: string;
    contact_title: string;
    name: string;
    location: string;
    city: string;
    province_code: string;
    postal: string;
    country_code: string;
}

interface IPersonalDetail {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    photo: string;
    city: string;
    province_code: string;
    postal: string;
    country_code: string;
}

interface ITemplate {
    id: number;
    name: string;
    type: string;
    recommended: boolean;
    image: string;
    html: string;
    created_at: string;
    updated_at: string;
}

export interface ICoverLetter {
    id: number;
    title: string;
    resume_id: number;
    template_id: number;
    personal_detail_id: number;
    job_title: string;
    job_description: string;
    created_at: string;
    updated_at: string;
    deleted: boolean;
    template: ITemplate;
    medias: IMedia[];
    personal_detail: IPersonalDetail;
    cover_letter_contents: ICoverLetterContent[];
    company_detail: ICompanyDetail;
}

interface IInterviewScanCriteriaPoint {
    id: number;
    scan_criteria_id: number;
    title: string;
    description: string;
    interview_result: {
        id: number;
        interview_question_id: number;
        criteria_point_id: number;
        ai_review: string;
        level: ScanCriteriaLevel;
        score: number;
    };
}

interface IInterviewScanCriteria {
    id: number;
    title: string;
    description: string;
    type: ScanCriteriaType;
    level: ScanCriteriaLevel;
    criteria_points: IInterviewScanCriteriaPoint[];
}

export interface IInterviewQuestion {
    id: number;
    interview_id: number;
    question: string;
    order: number;
    answer: string;
    summary: string;
    score: number;
    media: IMedia;
    interview_scan_criterias: IInterviewScanCriteria[];
}

export interface IInterview {
    id: number;
    resume_id: number;
    job_title: string;
    job_description: string;
    question_type: string;
    mode: string;
    created_at: string;
    updated_at: string;
    deleted: boolean;
    progress: number;
    overall_score: number;
    interview_questions: IInterviewQuestion[];
}

interface ISummary {
    id: number;
    resume_id: number;
    original: string;
    ai: string;
    selection: string;
    type: string;
    hidden: boolean;
}

interface IAchievement {
    id: number;
    resume_id: number;
    title: string;
    description: string;
    hidden: boolean;
}

interface ICertification {
    id: number;
    resume_id: number;
    title: string;
    description: string;
    url: string;
    hidden: boolean;
}

interface ISkill {
    id: number;
    resume_id: number;
    title: string;
}

interface ISocialLink {
    id: number;
    resume_id: number;
    type: string;
    url: string;
    hidden: boolean;
}

interface IEducationHighlight {
    id: number;
    source_id: number;
    source: string;
    original: string;
    ai: string;
    selection: string;
    order: number;
    hidden: boolean;
}

interface IEducation {
    id: number;
    resume_id: number;
    institution: string;
    institute_url: string;
    degree: string;
    location: string;
    start: string;
    end: string;
    gpa: string;
    hidden: boolean;
    highlights: IEducationHighlight[];
}

interface IWorkExperienceHighlight {
    id: number;
    source_id: number;
    source: string;
    original: string;
    ai: string;
    selection: string;
    order: number;
    hidden: boolean;
}

interface IWorkExperience {
    id: number;
    resume_id: number;
    title: string;
    description: string;
    company: string;
    company_url: string;
    location: string;
    start: string;
    end: string;
    hidden: boolean;
    highlights: IWorkExperienceHighlight[];
}

interface IProjectHighlight {
    id: number;
    source_id: number;
    source: string;
    original: string;
    ai: string;
    selection: string;
    order: number;
    hidden: boolean;
}

interface IProject {
    id: number;
    resume_id: number;
    title: string;
    description: string;
    url: string;
    company: string;
    start: string;
    end: string;
    hidden: boolean;
    highlights: IProjectHighlight[];
}

export interface IResumeBuild {
    id: number;
    instance_id: number;
    title: string;
    template_id: number;
    personal_detail_id: number;
    job_title: string;
    job_description: string;
    created_at: string;
    updated_at: string;
    deleted: boolean;
    template: ITemplate;
    personal_detail: IPersonalDetail;
    summaries: ISummary[];
    medias: IMedia[];
    achievements: IAchievement[];
    certifications: ICertification[];
    skills: ISkill[];
    social_links: ISocialLink[];
    educations: IEducation[];
    work_experiences: IWorkExperience[];
    projects: IProject[];
}

interface IScanCriteriaResult {
    id: number;
    title: string;
    description: string;
    type: ScanCriteriaType;
    level: ScanCriteriaLevel;
    criteria_points: IScanCriteriaPoint[];
}

interface IScanCriteriaPoint {
    id: number;
    scan_criteria_id: number;
    title: string;
    description: string;
    scan_result: {
        id: number;
        resume_scan_id: number;
        criteria_point_id: number;
        ai_review: string;
        level: ScanCriteriaLevel;
        score: number;
    };
}

interface IResume {
    id: number;
    instance_id: number;
    title: string;
    template_id: number | null;
    personal_detail_id: number;
    job_title: string;
    job_description: string;
    deleted: boolean;
    created_at: string;
    updated_at: string;
}

export interface IResumeScan {
    id: number;
    resume_id: number;
    job_title: string;
    job_description: string;
    created_at: string;
    updated_at: string;
    deleted: boolean;
};

export interface IResumeScanner {
    resume: IResume;
    resume_scan: IResumeScan;
    media_id: number;
    media: IMedia;
    overall_score: number;
    scan_criteria_results: IScanCriteriaResult[];
}

export interface ICareersData {
    resume_builds: IResumeBuild[],
    resume_scans: IResumeScanner[],
    cover_letters: ICoverLetter[],
    interviews: IInterview[]
}

export interface ICareersMedia {
    id: number;
    name: string;
    source_id: number;
    source: string;
    source_type: string;
    url: string;
}

interface IModuleFeatureWithSubscription extends EtyModuleFeature {
    subscribed_module_feature: EtySubscribedModuleFeature | null;
};

export interface IModuleWithFeatureAndSubscription extends IModuleWithFeatures {
    module_features: IModuleFeatureWithSubscription[];
};

export enum ChanceResultChance {
    VERY_GOOD = "very-good",
    GOOD = "good",
    NEUTRAL = "neutral",
    BAD = "bad",
    VERY_BAD = "very-bad",
};

export enum ChanceResultDisplay {
    GOOD = "good",
    BAD = "bad",
    NONE = "none",
};

export enum ApplicantVisaChance {
    HIGH = "high",
    MEDIUM = "medium",
    LOW = "low",
};

export interface IChanceResult {
    id: number;
    applicant_chance_id: number;
    chance_function_id: number;
    chance: ChanceResultChance;
    score: number;
    display: ChanceResultDisplay;
    title: string;
    description: string;
};

export interface IApplicantChance {
    id: number;
    applicant_id: number;
    questionnaire_instance_id: number;
    chance_id: number;
    score: number | null;
    chance: ApplicantVisaChance | null;
    chance_results: IChanceResult[];
};

export enum EImmigrationDocumentStatus {
    PENDING = "pending",
    APPROVED = "approved",
    REFUSED = "refused",
}

export enum EImmigrationDocumentSystemStatus {
    EXPIRING = "expiring",
    EXPIRED = "expired",
    UNKNOWN = "unknown",
    VALID = "valid",
}

export enum EImmigrationDocumentRenewalStatus {
    INVALID = "invalid",
    CURRENT = "current",
    TAKE_ACTION = "take-action",
    OPTIONS = "options",
    APPLYING = "applying",
    APPLIED = "applied",
    APPROVED = "approved",
    REFUSED = "refused",
}

export enum EDocumentType {
    PASSPORT = "passport",
    VISA = "visa",
    PERMIT = "permit",
}

export enum EVisaType {
    VISITOR = "visitor",
    WORK = "work",
    STUDY = "study",
    SUPER = "super",
    OTHER = "other",
    NOT_SURE = "not-sure",
}

export interface IDocument {
    id: number;
    type: EDocumentType;
    original: string;
    display: string;
    upload_timestamp: string;
}

export interface IPassportDetail {
    id: number;
    document_id: number | null;
    document: IDocument | null;
    passport_id: number;
    passport_number: string;
    nationality: string | null;
    issued: string | null;
    expire: string | null;
    submission_date: string | null;
    refused_date: string | null;
    effective_date: string;
    status: EImmigrationDocumentStatus;
    system_status: EImmigrationDocumentSystemStatus;
    renewal_status: EImmigrationDocumentRenewalStatus;
    renewal_comment: string | null;
}

export interface IVisaDetail {
    id: number;
    document_id: number | null;
    document: IDocument | null;
    passport_number: string;
    visa_id: number;
    visa_number: string;
    type: EVisaType;
    issued: string | null;
    expire: string | null;
    submission_date: string | null;
    refused_date: string | null;
    effective_date: string | null;
    status: EImmigrationDocumentStatus;
    system_status: EImmigrationDocumentSystemStatus;
    renewal_status: EImmigrationDocumentRenewalStatus;
    renewal_comment: string | null;
}

export interface IPermitDetail {
    id: number;
    document_id: number | null;
    document: IDocument | null;
    passport_number: string;
    permit_id: number;
    permit_number: string;
    permit_type_id: number;
    permit_type_description: string;
    permit_type_code: string;
    permit_type_status: string;
    issued: string | null;
    expire: string | null;
    submission_date: string | null;
    refused_date: string | null;
    effective_date: string | null;
    case_type: string;
    lmia: string;
    status: EImmigrationDocumentStatus;
    system_status: EImmigrationDocumentSystemStatus;
    renewal_status: EImmigrationDocumentRenewalStatus;
    renewal_comment: string | null;
    company: string;
}

export interface IStudentImmigrationDocuments {
    passports: IPassportDetail[];
    visas: IVisaDetail[];
    permits: IPermitDetail[];
};